import JSZip from 'jszip';
import { PlaceholderEnum } from '../../placeholders-panel';
import { VARIABLE_KEY_PATTERN_SOURCE } from './variable-utils';

const KNOWN_VARIABLES = Object.values(PlaceholderEnum);

const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

interface RelMap {
  images: Map<string, string>;
  hyperlinks: Map<string, string>;
}

function extToMime(ext: string): string {
  switch (ext.toLowerCase()) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'svg':
      return 'image/svg+xml';
    case 'webp':
      return 'image/webp';
    case 'bmp':
      return 'image/bmp';
    case 'tiff':
    case 'tif':
      return 'image/tiff';
    default:
      return `image/${ext}`;
  }
}

async function buildRelMap(zip: JSZip, relsXml: string | undefined): Promise<RelMap> {
  const images = new Map<string, string>();
  const hyperlinks = new Map<string, string>();

  if (!relsXml) return { images, hyperlinks };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(relsXml, 'application/xml');

    for (const rel of Array.from(doc.getElementsByTagName('Relationship'))) {
      const id = rel.getAttribute('Id');
      const type = rel.getAttribute('Type') || '';
      const target = rel.getAttribute('Target');
      if (!id || !target) continue;

      if (type.endsWith('/image')) {
        const path = target.startsWith('/') ? target.slice(1) : `word/${target}`;
        const file = zip.file(path);
        if (!file) continue;
        const base64 = await file.async('base64');
        const ext = path.split('.').pop() || 'png';
        const mime = extToMime(ext);
        images.set(id, `data:${mime};base64,${base64}`);
      } else if (type.endsWith('/hyperlink')) {
        hyperlinks.set(id, target);
      }
    }
  } catch (e) {
    console.warn('Erro ao parsear document.xml.rels:', e);
  }

  return { images, hyperlinks };
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function sanitizeHref(href: string): string | null {
  const trimmed = href.trim();
  const scheme = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/)?.[1]?.toLowerCase();
  if (scheme && !['http', 'https', 'mailto', 'tel'].includes(scheme)) return null;
  return trimmed;
}

function extractImageFromRun(r: Element, imageMap: Map<string, string>): string | null {
  const blip = r.querySelector('a\\:blip, blip');
  if (!blip) return null;

  let rId = blip.getAttribute('r:embed');
  if (!rId) rId = blip.getAttributeNS(REL_NS, 'embed');
  if (!rId) return null;

  const src = imageMap.get(rId);
  if (!src) return null;

  const extent = r.querySelector('wp\\:extent, extent');
  const attrs: string[] = [`src="${src}"`];

  if (extent) {
    const cx = parseInt(extent.getAttribute('cx') || '0');
    const cy = parseInt(extent.getAttribute('cy') || '0');
    // EMU → px: 914400 EMU = 1 inch = 96 px → 9525 EMU = 1 px
    if (cx > 0) attrs.push(`width="${Math.round(cx / 9525)}"`);
    if (cy > 0) attrs.push(`height="${Math.round(cy / 9525)}"`);
  }

  const docPr = r.querySelector('wp\\:docPr, docPr');
  const alt = docPr?.getAttribute('descr') || docPr?.getAttribute('title') || '';
  if (alt) attrs.push(`alt="${escapeAttr(alt)}"`);

  // Detecta float via wp:anchor com wrapSquare/wrapTight
  const anchor = r.querySelector('wp\\:anchor, anchor');
  if (anchor) {
    const hasWrap = anchor.querySelector(
      'wp\\:wrapSquare, wrapSquare, wp\\:wrapTight, wrapTight'
    );
    if (hasWrap) {
      const positionH = anchor.querySelector('wp\\:positionH, positionH');
      const positionV = anchor.querySelector('wp\\:positionV, positionV');
      const hAlignEl = positionH?.querySelector('wp\\:align, align');
      const hOffsetEl = positionH?.querySelector('wp\\:posOffset, posOffset');
      const vOffsetEl = positionV?.querySelector('wp\\:posOffset, posOffset');

      // Posição livre: ambos H e V usam posOffset numérico → data-pos-x/y em px
      if (hOffsetEl && vOffsetEl && !hAlignEl) {
        const xEmu = parseInt(hOffsetEl.textContent?.trim() || '0', 10);
        const yEmu = parseInt(vOffsetEl.textContent?.trim() || '0', 10);
        if (isFinite(xEmu) && isFinite(yEmu)) {
          const xPx = Math.round(xEmu / 9525);
          const yPx = Math.round(yEmu / 9525);
          attrs.push(`data-pos-x="${xPx}"`);
          attrs.push(`data-pos-y="${yPx}"`);
        }
      } else {
        // Float clássico left/right
        const alignText = hAlignEl?.textContent?.trim();
        let floatSide: 'left' | 'right';
        if (alignText === 'right') floatSide = 'right';
        else if (alignText === 'left') floatSide = 'left';
        else floatSide = 'left';
        attrs.push(`data-float="${floatSide}"`);
      }
    }
  }

  return `<img ${attrs.join(' ')}/>`;
}

/**
 * Converte texto {variavel} em spans data-variable no HTML.
 * Variáveis do PlaceholderEnum viram chips normais; outras chaves no formato
 * {minha_chave} viram chips customizados (data-custom="true") para que o usuário
 * possa preenchê-las no momento de gerar o contrato.
 */
function convertVariablesToNodes(html: string): string {
  const variablePattern = new RegExp(`\\{(${VARIABLE_KEY_PATTERN_SOURCE})\\}`, 'gi');

  return html.replace(variablePattern, (_match, name) => {
    const lowerName = name.toLowerCase();
    if (KNOWN_VARIABLES.includes(lowerName as PlaceholderEnum)) {
      return `<span data-variable="${lowerName}" class="variable-chip" contenteditable="false">{${lowerName}}</span>`;
    }
    return `<span data-variable="${lowerName}" data-custom="true" class="variable-chip variable-chip--custom" contenteditable="false">{${lowerName}}</span>`;
  });
}

// ─── Mapeamento de alinhamento OOXML → CSS ───
const ALIGNMENT_MAP: Record<string, string> = {
  left: 'left',
  center: 'center',
  right: 'right',
  both: 'justify',
  justify: 'justify',
};

// ─── Mapeamento de heading levels ───
const HEADING_STYLES: Record<string, string> = {
  'Heading1': 'h1',
  'Heading2': 'h2',
  'Heading3': 'h3',
  'Heading4': 'h4',
  'heading 1': 'h1',
  'heading 2': 'h2',
  'heading 3': 'h3',
  'heading 4': 'h4',
};

/**
 * Converte twips (twentieths of a point) para px.
 * 1 twip = 1/20 pt, 1 pt = 1.333 px
 */
function twipsToPx(twips: number): number {
  return Math.round((twips / 20) * 1.333);
}

/**
 * Extrai o valor de um atributo w:val de um elemento XML.
 */
function getVal(el: Element | null): string | null {
  return el?.getAttribute('w:val') ?? null;
}

/**
 * Verifica se uma propriedade booleana OOXML está ativa.
 * Em OOXML, a presença da tag sem w:val ou com w:val != "false"/"0" significa ativo.
 */
function isBooleanPropActive(rPr: Element, selector: string): boolean {
  const el = rPr.querySelector(selector);
  if (!el) return false;
  const val = el.getAttribute('w:val');
  if (val === null) return true; // tag presente sem valor = ativo
  return val !== 'false' && val !== '0';
}

/**
 * Extrai estilos inline de um run (w:rPr).
 */
function getRunStyles(rPr: Element | null): string[] {
  const tags: string[] = [];
  if (!rPr) return tags;

  if (isBooleanPropActive(rPr, 'w\\:b, b')) tags.push('bold');
  if (isBooleanPropActive(rPr, 'w\\:i, i')) tags.push('italic');
  if (isBooleanPropActive(rPr, 'w\\:strike, strike')) tags.push('strike');

  const uEl = rPr.querySelector('w\\:u, u');
  if (uEl) {
    const uVal = uEl.getAttribute('w:val');
    if (uVal && uVal !== 'none') tags.push('underline');
  }

  return tags;
}

/**
 * Extrai a cor do texto do run.
 */
function getRunColor(rPr: Element | null): string | null {
  if (!rPr) return null;
  const colorEl = rPr.querySelector('w\\:color, color');
  if (!colorEl) return null;
  const val = colorEl.getAttribute('w:val');
  if (val && val !== 'auto' && val !== '000000') return `#${val}`;
  return null;
}

/**
 * Extrai a fonte do run.
 */
function getRunFont(rPr: Element | null): string | null {
  if (!rPr) return null;
  const fontEl = rPr.querySelector('w\\:rFonts, rFonts');
  if (!fontEl) return null;
  return fontEl.getAttribute('w:ascii') || fontEl.getAttribute('w:hAnsi') || null;
}

/**
 * Extrai o highlight/cor de fundo do run.
 */
function getRunHighlight(rPr: Element | null): string | null {
  if (!rPr) return null;
  const hlEl = rPr.querySelector('w\\:highlight, highlight');
  if (!hlEl) return null;
  const val = getVal(hlEl);
  if (val && val !== 'none') return val;
  return null;
}

/**
 * Envolve texto com tags de formatação.
 */
function wrapWithFormatting(text: string, styles: string[], color: string | null, font: string | null, highlight: string | null): string {
  let result = text;

  if (styles.includes('bold')) result = `<strong>${result}</strong>`;
  if (styles.includes('italic')) result = `<em>${result}</em>`;
  if (styles.includes('underline')) result = `<u>${result}</u>`;
  if (styles.includes('strike')) result = `<s>${result}</s>`;

  const inlineStyles: string[] = [];
  if (color) inlineStyles.push(`color: ${color}`);
  if (font) inlineStyles.push(`font-family: ${font}`);

  if (highlight) {
    result = `<mark data-color="${highlight}" style="background-color: ${highlight}">${result}</mark>`;
  }

  if (inlineStyles.length > 0) {
    result = `<span style="${inlineStyles.join('; ')}">${result}</span>`;
  }

  return result;
}

/**
 * Processa um parágrafo OOXML (w:p) e retorna HTML.
 */
function processParagraph(p: Element, numbering: Map<string, { numFmt: string }>, styles: Map<string, Element>, rels: RelMap): string {
  const pPr = p.querySelector(':scope > w\\:pPr, :scope > pPr');

  // Verificar se é heading pelo estilo
  let tag = 'p';
  const pStyleEl = pPr?.querySelector('w\\:pStyle, pStyle');
  const styleName = getVal(pStyleEl ?? null) || '';

  // Verificar heading por estilo direto
  if (HEADING_STYLES[styleName]) {
    tag = HEADING_STYLES[styleName];
  }

  // Verificar heading por outline level
  if (tag === 'p' && pPr) {
    const outlineLvl = pPr.querySelector('w\\:outlineLvl, outlineLvl');
    const level = outlineLvl ? parseInt(getVal(outlineLvl) || '-1') : -1;
    if (level >= 0 && level <= 3) {
      tag = `h${level + 1}`;
    }
  }

  // Extrair alinhamento
  const jcEl = pPr?.querySelector('w\\:jc, jc');
  const alignment = jcEl ? ALIGNMENT_MAP[getVal(jcEl) || ''] : null;

  // Extrair espaçamento
  const spacingEl = pPr?.querySelector('w\\:spacing, spacing');
  const spaceBefore = spacingEl?.getAttribute('w:before');
  const spaceAfter = spacingEl?.getAttribute('w:after');
  const lineSpacing = spacingEl?.getAttribute('w:line');
  const lineRule = spacingEl?.getAttribute('w:lineRule');

  // Extrair indentação
  const indEl = pPr?.querySelector('w\\:ind, ind');
  const indLeft = indEl?.getAttribute('w:left') || indEl?.getAttribute('w:start');
  const indRight = indEl?.getAttribute('w:right') || indEl?.getAttribute('w:end');
  const indFirstLine = indEl?.getAttribute('w:firstLine');
  const indHanging = indEl?.getAttribute('w:hanging');

  // Verificar se é lista (w:numPr)
  const numPr = pPr?.querySelector('w\\:numPr, numPr');
  let isList = false;
  let listTag = 'ul';
  if (numPr) {
    const numId = getVal(numPr.querySelector('w\\:numId, numId'));
    if (numId && numId !== '0') {
      isList = true;
      const numInfo = numbering.get(numId);
      if (numInfo && (numInfo.numFmt === 'decimal' || numInfo.numFmt === 'lowerLetter' || numInfo.numFmt === 'upperLetter' || numInfo.numFmt === 'lowerRoman' || numInfo.numFmt === 'upperRoman')) {
        listTag = 'ol';
      }
    }
  }

  // Construir estilos inline
  const inlineStyles: string[] = [];
  if (alignment) inlineStyles.push(`text-align: ${alignment}`);
  if (spaceBefore) inlineStyles.push(`margin-top: ${twipsToPx(parseInt(spaceBefore))}px`);
  if (spaceAfter) inlineStyles.push(`margin-bottom: ${twipsToPx(parseInt(spaceAfter))}px`);

  if (lineSpacing && lineRule) {
    if (lineRule === 'auto') {
      // line spacing em 240ths de uma linha (240 = single)
      const factor = parseInt(lineSpacing) / 240;
      if (factor !== 1) {
        inlineStyles.push(`line-height: ${factor.toFixed(2)}`);
      }
    } else if (lineRule === 'exact' || lineRule === 'atLeast') {
      inlineStyles.push(`line-height: ${twipsToPx(parseInt(lineSpacing))}px`);
    }
  }

  if (indLeft) inlineStyles.push(`margin-left: ${twipsToPx(parseInt(indLeft))}px`);
  if (indRight) inlineStyles.push(`margin-right: ${twipsToPx(parseInt(indRight))}px`);
  if (indFirstLine) inlineStyles.push(`text-indent: ${twipsToPx(parseInt(indFirstLine))}px`);
  if (indHanging) inlineStyles.push(`text-indent: -${twipsToPx(parseInt(indHanging))}px`);

  const styleAttr = inlineStyles.length > 0 ? ` style="${inlineStyles.join('; ')}"` : '';

  // Processar runs (w:r)
  let content = '';

  // Buscar estilos herdados do estilo do parágrafo
  const inheritedRPr = styles.get(styleName)?.querySelector('w\\:rPr, rPr') || null;
  const pRunStyles = getRunStyles(inheritedRPr);

  for (const child of Array.from(p.children)) {
    const childTag = child.tagName.toLowerCase().replace(/^w:/, '');

    if (childTag === 'r') {
      // Detectar imagem (w:drawing) antes de tratar como texto
      const imgHtml = extractImageFromRun(child, rels.images);
      if (imgHtml) {
        content += imgHtml;
        continue;
      }

      const rPr = child.querySelector(':scope > w\\:rPr, :scope > rPr');
      const runStyles = getRunStyles(rPr);
      const allStyles = [...new Set([...pRunStyles, ...runStyles])];
      const color = getRunColor(rPr);
      const font = getRunFont(rPr);
      const highlight = getRunHighlight(rPr);

      // Coletar texto de todos os w:t dentro do run
      const textEls = child.querySelectorAll('w\\:t, t');
      let text = '';
      for (const t of Array.from(textEls)) {
        text += t.textContent || '';
      }

      // Verificar se é um tab
      const tabEls = child.querySelectorAll('w\\:tab, tab');
      if (tabEls.length > 0 && !text) {
        text = '\t';
      }

      // Verificar se é um break
      const brEls = child.querySelectorAll('w\\:br, br');
      if (brEls.length > 0) {
        content += '<br/>';
      }

      if (text) {
        // Escapar HTML
        const escaped = text
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');

        content += wrapWithFormatting(escaped, allStyles, color, font, highlight);
      }
    } else if (childTag === 'hyperlink') {
      // Resolver href via rels
      let hrefRId = child.getAttribute('r:id');
      if (!hrefRId) hrefRId = child.getAttributeNS(REL_NS, 'id');
      const href = hrefRId ? rels.hyperlinks.get(hrefRId) : null;

      let linkContent = '';
      for (const r of Array.from(child.querySelectorAll('w\\:r, r'))) {
        // Imagem dentro de hyperlink (raro, mas suportado)
        const imgHtml = extractImageFromRun(r, rels.images);
        if (imgHtml) {
          linkContent += imgHtml;
          continue;
        }

        const rPr = r.querySelector(':scope > w\\:rPr, :scope > rPr');
        const runStyles = getRunStyles(rPr);
        const allStyles = [...new Set([...pRunStyles, ...runStyles])];
        const color = getRunColor(rPr);
        const font = getRunFont(rPr);
        const highlight = getRunHighlight(rPr);

        const textEls = r.querySelectorAll('w\\:t, t');
        let text = '';
        for (const t of Array.from(textEls)) text += t.textContent || '';

        if (text) {
          const escaped = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
          linkContent += wrapWithFormatting(escaped, allStyles, color, font, highlight);
        }
      }

      const safeHref = href ? sanitizeHref(href) : null;
      if (safeHref && linkContent) {
        content += `<a href="${escapeAttr(safeHref)}">${linkContent}</a>`;
      } else if (linkContent) {
        content += linkContent;
      }
    }
  }

  if (isList) {
    return `<${listTag}><li${styleAttr}>${content}</li></${listTag}>`;
  }

  // Parágrafo cujo único conteúdo é uma imagem: promove w:jc para data-align
  // no <img> e descarta o <p> envolvente. Imagens já com data-float ou
  // data-pos-x (anchor) não recebem data-align — são mutuamente exclusivos.
  if (tag === 'p') {
    const trimmed = content.trim();
    const isLoneImg = /^<img\b[^>]*\/?>$/.test(trimmed);
    if (isLoneImg) {
      const hasFloat = /\bdata-float=/.test(trimmed);
      const hasPos = /\bdata-pos-x=/.test(trimmed);
      if (
        !hasFloat &&
        !hasPos &&
        alignment &&
        (alignment === 'left' || alignment === 'center' || alignment === 'right')
      ) {
        return trimmed.replace(/\/?>$/, ` data-align="${alignment}"/>`);
      }
      return trimmed;
    }
  }

  return `<${tag}${styleAttr}>${content}</${tag}>`;
}

/**
 * Processa uma tabela OOXML (w:tbl) e retorna HTML.
 */
function processTable(tbl: Element, numbering: Map<string, { numFmt: string }>, styles: Map<string, Element>, rels: RelMap): string {
  let html = '<table><tbody>';

  const rows = tbl.querySelectorAll(':scope > w\\:tr, :scope > tr');
  for (const tr of Array.from(rows)) {
    html += '<tr>';
    const cells = tr.querySelectorAll(':scope > w\\:tc, :scope > tc');
    for (const tc of Array.from(cells)) {
      html += '<td>';
      const paragraphs = tc.querySelectorAll(':scope > w\\:p, :scope > p');
      for (const p of Array.from(paragraphs)) {
        html += processParagraph(p, numbering, styles, rels);
      }
      html += '</td>';
    }
    html += '</tr>';
  }

  html += '</tbody></table>';
  return html;
}

/**
 * Parseia o numbering.xml para extrair tipos de listas.
 */
function parseNumbering(xml: string): Map<string, { numFmt: string }> {
  const result = new Map<string, { numFmt: string }>();

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');

    const abstractNums = new Map<string, string>();
    for (const abstractNum of Array.from(doc.querySelectorAll('w\\:abstractNum, abstractNum'))) {
      const id = abstractNum.getAttribute('w:abstractNumId');
      const lvl = abstractNum.querySelector('w\\:lvl, lvl');
      const numFmt = lvl?.querySelector('w\\:numFmt, numFmt');
      if (id && numFmt) {
        abstractNums.set(id, getVal(numFmt) || 'bullet');
      }
    }

    for (const num of Array.from(doc.querySelectorAll('w\\:num, num'))) {
      const numId = num.getAttribute('w:numId');
      const abstractNumId = num.querySelector('w\\:abstractNumId, abstractNumId');
      const refId = getVal(abstractNumId);
      if (numId && refId && abstractNums.has(refId)) {
        result.set(numId, { numFmt: abstractNums.get(refId)! });
      }
    }
  } catch (e) {
    console.warn('Erro ao parsear numbering.xml:', e);
  }

  return result;
}

/**
 * Parseia o styles.xml para extrair estilos de parágrafo.
 */
function parseStyles(xml: string): Map<string, Element> {
  const result = new Map<string, Element>();

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, 'application/xml');

    for (const style of Array.from(doc.querySelectorAll('w\\:style, style'))) {
      const styleId = style.getAttribute('w:styleId');
      if (styleId) {
        result.set(styleId, style);
      }
    }
  } catch (e) {
    console.warn('Erro ao parsear styles.xml:', e);
  }

  return result;
}

/**
 * Mescla itens de lista consecutivos em listas únicas.
 * Converte `<ul><li>A</li></ul><ul><li>B</li></ul>` em `<ul><li>A</li><li>B</li></ul>`
 */
function mergeConsecutiveLists(html: string): string {
  return html
    .replace(/<\/ul>\s*<ul>/gi, '')
    .replace(/<\/ol>\s*<ol>/gi, '');
}

/**
 * Importa um arquivo DOCX e retorna HTML com variáveis convertidas em nodes.
 * Parser customizado que preserva alinhamento, espaçamento e indentação.
 */
export async function importDocx(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Ler o documento principal
  const documentXml = await zip.file('word/document.xml')?.async('string');
  if (!documentXml) {
    throw new Error('Arquivo DOCX inválido: word/document.xml não encontrado');
  }

  // Ler numbering.xml (opcional, para listas)
  const numberingXml = await zip.file('word/numbering.xml')?.async('string');
  const numbering = numberingXml ? parseNumbering(numberingXml) : new Map();

  // Ler styles.xml (opcional, para estilos herdados)
  const stylesXml = await zip.file('word/styles.xml')?.async('string');
  const styles = stylesXml ? parseStyles(stylesXml) : new Map();

  // Ler document.xml.rels (para imagens e hyperlinks)
  const relsXml = await zip.file('word/_rels/document.xml.rels')?.async('string');
  const rels = await buildRelMap(zip, relsXml);

  // Parsear o document.xml
  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, 'application/xml');

  // Encontrar o body
  const body = doc.querySelector('w\\:body, body');
  if (!body) {
    throw new Error('Arquivo DOCX inválido: body não encontrado');
  }

  let html = '';

  for (const child of Array.from(body.children)) {
    const tag = child.tagName.toLowerCase().replace(/^w:/, '');

    if (tag === 'p') {
      html += processParagraph(child, numbering, styles, rels);
    } else if (tag === 'tbl') {
      html += processTable(child, numbering, styles, rels);
    }
    // Ignora outros elementos (sectPr, etc.)
  }

  html = mergeConsecutiveLists(html);

  const htmlWithVariables = convertVariablesToNodes(html);

  return htmlWithVariables;
}
